import {
  FormControl as MUIFormControl,
  RadioGroup as MUIRadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';
import { Controller, FieldValues, useFormContext } from 'react-hook-form';

import { Alert } from '@/shared/components/ui/alert/alert';
import { FormErrorMessage } from '@/shared/components/ui/form/components/form-error-message/form-error-message';
import { FormLabel } from '@/shared/components/ui/form/components/form-label/form-label';
import { Radio } from '@/shared/components/ui/form/components/radio/radio';
import { RadioGroupOption } from '@/shared/components/ui/form/components/radio-group/radio-group.styled';
import {
  TRadioGroupProps,
  TRadioOption,
} from '@/shared/components/ui/form/components/radio-group/radio-group.types';
import { TruncatedText } from '@/shared/components/ui/truncated-text/truncated-text';

const DESCRIPTION_MAX_LENGTH = 40;

export const RadioGroup = <
  TFieldValues extends FieldValues,
  TValue extends string | number = string,
>({
  name,
  label,
  required,
  disabled,
  defaultValue,
  options,
  optionDirection = 'row',
  hint,
  onChange,
}: TRadioGroupProps<TFieldValues, TValue>) => {
  const { control } = useFormContext<TFieldValues>();

  // Described options need the room a fixed-width truncated label doesn't give.
  const hasDescriptions = options.some((option) => option.description);

  // Options in a row share the width, so a description that wraps leaves the
  // cards different heights; truncate it and reveal the rest on hover.
  const renderDescription = (description: string) => {
    if (optionDirection === 'row') {
      return (
        <TruncatedText
          variant="p9"
          color="textAdditional"
          maxLength={DESCRIPTION_MAX_LENGTH}
          text={description}
        />
      );
    }

    return (
      <Typography variant="p9" color="textAdditional">
        {description}
      </Typography>
    );
  };

  const renderOptionLabel = (option: TRadioOption<TValue>) => {
    if (!option.description) {
      return <TruncatedText variant="p8" maxLength={14} text={option.label} />;
    }

    return (
      <Stack padding="0.25rem 0">
        <Typography variant="p8">{option.label}</Typography>
        {renderDescription(option.description)}
      </Stack>
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({
        field: { onChange: onFieldChange, value, ...fieldProps },
        fieldState,
      }) => (
        <MUIFormControl>
          <FormLabel
            error={Boolean(fieldState?.error)}
            label={label}
            required={required}
          />
          <MUIRadioGroup
            value={value ?? ''}
            onChange={(event, value) => {
              onFieldChange(event, value);
              onChange?.();
            }}
            {...fieldProps}
          >
            <Stack
              direction={optionDirection}
              gap={optionDirection === 'row' ? '1rem' : '0.5rem'}
              marginBottom={optionDirection === 'row' ? '-1rem' : 0}
            >
              {options.map((option) => (
                <RadioGroupOption
                  key={option.value}
                  $error={Boolean(fieldState?.error)}
                  $wide={hasDescriptions}
                  value={option.value}
                  control={<Radio disabled={disabled} />}
                  label={renderOptionLabel(option)}
                />
              ))}
            </Stack>
          </MUIRadioGroup>
          {(fieldState?.error || hint) && (
            <Stack
              gap="0.5rem"
              marginTop={optionDirection === 'row' ? '-0.25rem' : '0.75rem'}
            >
              {fieldState?.error && <FormErrorMessage field={label} />}
              {hint && <Alert color="blue" text={hint} />}
            </Stack>
          )}
        </MUIFormControl>
      )}
    />
  );
};
